(() => {
  'use strict';
const JACKET_VARIANTS = Object.freeze([
  '51907756687541', '51907756720309', '51907756753077',
  '51907756785845', '51907756818613', '51907756851381',
]);
const CAP_VARIANT = '51903076761781';
const OPERATION = '_otf_peachtree_operation';
const ROLE = '_otf_peachtree_role';
const id = line => String(line.variant_id ?? line.id);
const lines = cart => {
  if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
  return cart.items.filter(line => Number(line.quantity) > 0);
};
const parentKey = line => line.parent_relationship?.parent_key;
const owns = (line, token) => line.properties?.[OPERATION] === token && (
  (line.properties?.[ROLE] === 'jacket' && JACKET_VARIANTS.includes(id(line))) ||
  (line.properties?.[ROLE] === 'cap' && id(line) === CAP_VARIANT)
);

const giftInteger = value => {
  if (typeof value !== 'number' && !(typeof value === 'string' && /^\d+$/.test(value))) return null;
  const amount = Number(value);
  return Number.isSafeInteger(amount) && amount >= 0 ? amount : null;
};
const approvedGiftAllocation = (allocation, title) => {
  const application = allocation?.discount_application;
  if (!application || Array.isArray(application) || application.title !== title) return false;
  if (application.type != null && typeof application.type !== 'string') return false;
  const type = typeof application.type === 'string' ? application.type.trim().toLowerCase() : '';
  return type !== 'discount_code' && type !== 'manual';
};

function qualifyingGift(cart, discountTitle) {
  if (!discountTitle || !lines(cart).some(line => JACKET_VARIANTS.includes(id(line)))) return null;
  const caps = lines(cart).filter(line => id(line) === CAP_VARIANT);
  const allocated = caps.map(line => {
    const matched = (Array.isArray(line.line_level_discount_allocations) ? line.line_level_discount_allocations : [])
      .filter(allocation => approvedGiftAllocation(allocation, discountTitle));
    return {
      line,
      unitPrice: giftInteger(line.original_price ?? line.price),
      quantity: giftInteger(line.quantity),
      finalLinePrice: giftInteger(line.final_line_price),
      validAmounts: matched.every(allocation => giftInteger(allocation.amount) !== null),
      amount: matched.reduce((sum, allocation) => sum + (giftInteger(allocation.amount) ?? 0), 0),
    };
  });
  if (allocated.some(value => !value.validAmounts)) return null;
  const rewarded = allocated.filter(value => value.amount > 0);
  if (!rewarded.length || rewarded.some(value => value.unitPrice === null || value.unitPrice <= 0 || value.quantity === null || value.quantity <= 0 || value.finalLinePrice === null)) return null;
  if (rewarded.some(value => !Number.isSafeInteger(value.amount) || value.finalLinePrice !== value.unitPrice * value.quantity - value.amount)) return null;
  const amount = rewarded.reduce((sum, value) => sum + value.amount, 0);
  if (!rewarded.every(value => value.unitPrice === rewarded[0].unitPrice) || amount !== rewarded[0].unitPrice) return null;
  return {
    discountedQuantity: 1,
    allocationAmount: amount,
    currency: cart.currency,
    fullFreeLineKeys: rewarded.filter(value => value.quantity === 1 && value.finalLinePrice === 0).map(value => value.line.key),
  };
}

function planOffer(cart, selectedId, token) {
  const existing = lines(cart);
  const jacket = existing.find(line => id(line) === selectedId && !line.selling_plan_allocation);
  const cap = existing.find(line => id(line) === CAP_VARIANT && !line.selling_plan_allocation);
  const properties = role => ({ [OPERATION]: token, [ROLE]: role });
  const items = [];
  if (!jacket) items.push({ id: Number(selectedId), quantity: 1, properties: properties('jacket') });
  if (!cap) items.push({
    id: Number(CAP_VARIANT), quantity: 1, properties: properties('cap'),
    ...(jacket ? { parent_line_key: jacket.key } : { parent_id: Number(selectedId) }),
  });
  return { items, reusesJacket: !!jacket, reusesCap: !!cap };
}

function createOfferFlow({
  api, enabled = false, discountTitle = '', token = () => globalThis.crypto.randomUUID(),
  checkpoint = { read: () => null, write: () => {}, clear: () => {} }, diagnostic = () => {},
}) {
  const trace = (stage, values = {}) => { try { diagnostic(stage, values); } catch {} };
  let busy = false;
  const saved = checkpoint.read();
  let pending = typeof saved === 'string' ? { operation: saved, uncertain: true, expected: null } : saved;
  let releaseHold = pending ? api.holdCheckout() : null;
  const release = () => { releaseHold?.(); releaseHold = null; };
  const clear = () => { checkpoint.clear(); pending = null; release(); };
  const result = (status, message, extra = {}) => { trace('flow_result', { outcome: status, uncertain: !!pending?.uncertain }); return { status, message, ...extra }; };

  async function rollback(operation) {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const cart = await api.read();
      const current = lines(cart);
      const owned = current.filter(line => owns(line, operation));
      if (!owned.length) return cart;
      const removable = owned.find(line => !current.some(child => parentKey(child) === line.key && !owns(child, operation)));
      if (!removable?.key) throw new Error('Cannot safely remove related cart items');
      try { await api.change(removable.key, 0); }
      catch (error) {
        const checked = await api.read();
        if (lines(checked).some(line => line.key === removable.key && owns(line, operation))) throw error;
      }
    }
    throw new Error('Cart needs review');
  }

  const committed = (cart, pendingWrite) => Array.isArray(pendingWrite.expected) && pendingWrite.expected.length > 0 && pendingWrite.expected.every(expected =>
    lines(cart).some(line => owns(line, pendingWrite.operation) && id(line) === expected.variant && line.properties[ROLE] === expected.role && Number(line.quantity) === expected.quantity));

  async function recover() {
    if (busy) return result('busy', 'Please wait for your cart to update.');
    if (!pending) return result('unchanged', 'No offer recovery is needed.');
    if (api.isBusy?.()) return result('busy', 'Please wait for your cart to update before recovery.');
    busy = true;
    let finish;
    try {
      releaseHold ||= api.holdCheckout();
      finish = api.begin();
      if (pending.uncertain && !committed(await api.read(), pending)) return result('review_required', 'The earlier cart request is still unconfirmed. Keep this page open and retry recovery; do not add the offer again.');
      const cart = await rollback(pending.operation);
      clear();
      return result('unavailable', 'The incomplete offer was removed. Your existing items were kept.', { cart });
    } catch {
      return result('review_required', 'We could not verify the incomplete offer. Retry cart recovery before checkout.');
    } finally { finish?.(); busy = false; }
  }

  async function claim({ variantId, explicitSelection = false } = {}) {
    if (busy) return result('busy', 'Please wait for your cart to update.');
    if (pending) return result('review_required', 'Recover the previous cart update before trying again.');
    if (!enabled || !discountTitle) return result('disabled', 'This offer is not active.');
    const selected = String(variantId ?? '');
    if (!explicitSelection || !JACKET_VARIANTS.includes(selected)) return result('selection_required', 'Choose your jacket size first.');
    if (api.isBusy?.()) return result('busy', 'Please wait for your cart to update.');
    busy = true;
    let finish;
    let operation;
    let plan;
    let writeAttempted = false;
    try {
      releaseHold = api.holdCheckout();
      finish = api.begin();
      const before = await api.read();
      operation = token();
      if (!operation || lines(before).some(line => line.properties?.[OPERATION] === operation)) throw new Error('Invalid operation token');
      plan = planOffer(before, selected, operation);
      if (plan.items.length) {
        const availability = await api.availability(plan.items.map(item => String(item.id)));
        if (plan.items.some(item => availability[String(item.id)] !== true)) throw new Error('An offer item is unavailable');
        const record = { operation, uncertain: true, expected: plan.items.map(item => ({ variant: String(item.id), role: item.properties[ROLE], quantity: item.quantity })) };
        checkpoint.write(record); // Persist uncertainty before dispatch; no write if persistence fails.
        pending = record;
        writeAttempted = true;
        let settled = false;
        try {
          const response = await api.add(plan.items);
          const matchedCount = Array.isArray(response?.items) ? plan.items.filter(item => response.items.some(line => id(line) === String(item.id) && Number(line.quantity) === item.quantity && owns(line, operation) && line.properties[ROLE] === item.properties[ROLE])).length : 0;
          settled = Array.isArray(response?.items) && matchedCount === plan.items.length;
          trace('add_checked', { expectedCount: plan.items.length, returnedCount: Array.isArray(response?.items) ? response.items.length : 0, matchedCount, settled });
        }
        catch (error) { settled = error?.cartWriteOutcome === 'rejected'; trace('add_checked', { expectedCount: plan.items.length, settled, uncertain: !settled }); }
        if (settled) { const confirmed = { ...record, uncertain: false }; checkpoint.write(confirmed); pending = confirmed; }
      }
      const cart = await api.read();
      if (pending?.uncertain && committed(cart, pending)) {
        const confirmed = { ...pending, uncertain: false };
        checkpoint.write(confirmed);
        pending = confirmed;
        trace('write_confirmed', { expectedCount: pending.expected.length, committed: true, uncertain: false });
      }
      const hasSelected = lines(cart).some(line => id(line) === selected);
      const gift = qualifyingGift(cart, discountTitle);
      const ownedCaps = lines(cart).filter(line => owns(line, operation) && line.properties[ROLE] === 'cap');
      for (const cap of ownedCaps) {
        const allocations = Array.isArray(cap.line_level_discount_allocations) ? cap.line_level_discount_allocations : [];
        const titled = allocations.filter(value => value.discount_application?.title === discountTitle);
        const automatic = titled.filter(value => value.discount_application?.type === 'automatic');
        const matched = allocations.filter(value => approvedGiftAllocation(value, discountTitle));
        const amount = matched.reduce((sum, value) => sum + (giftInteger(value.amount) ?? 0), 0);
        const original = Number(cap.original_price ?? cap.price);
        const final = Number(cap.final_line_price);
        const quantity = Number(cap.quantity);
        trace('gift_cap_checked', { capOriginalCents: original, capPriceCents: Number(cap.price), capLineCents: Number(cap.line_price), capAllAllocationCents: allocations.reduce((sum, value) => sum + Number(value.amount), 0), capExpectedTitleDiscountCents: titled.reduce((sum, value) => sum + Number(value.amount), 0), capFinalLineCents: final, capQuantity: quantity, capAllocationCount: allocations.length, capTitleMatchCount: titled.length, capAutomaticMatchCount: automatic.length, capEligibleMatchCount: matched.length, capMatchedDiscountCents: amount, capOriginalPresent: cap.original_price != null, capLinePresent: cap.line_price != null, capAllAmountsValid: allocations.every(value => giftInteger(value.amount) !== null), capFinalPresent: cap.final_line_price != null, capAllocationArray: Array.isArray(cap.line_level_discount_allocations), capTitleMatches: titled.length > 0, capAutomaticMatches: automatic.length > 0, capEligibleMatches: matched.length > 0, capOriginalPositive: giftInteger(cap.original_price ?? cap.price) !== null && giftInteger(cap.original_price ?? cap.price) > 0, capAllocationAmountsValid: matched.every(value => giftInteger(value.amount) !== null), capArithmeticMatches: giftInteger(cap.original_price ?? cap.price) !== null && giftInteger(cap.quantity) !== null && giftInteger(cap.final_line_price) !== null && matched.every(value => giftInteger(value.amount) !== null) && Number.isSafeInteger(amount) && final === original * quantity - amount, capExactlyOneFreeUnit: giftInteger(cap.quantity) === 1 && giftInteger(cap.final_line_price) === 0 && giftInteger(cap.original_price ?? cap.price) > 0 && matched.every(value => giftInteger(value.amount) !== null) && amount === original });
      }
      const ownedCapsFree = ownedCaps.every(line => Number(line.quantity) === 1 && gift?.fullFreeLineKeys.includes(line.key));
      const nested = ownedCaps.every(line => !!parentKey(line) && lines(cart).some(parent => parent.key === parentKey(line) && id(parent) === selected));
      trace('cart_checked', { expectedCount: pending?.expected?.length || 0, matchedCount: pending?.expected?.filter(expected => lines(cart).some(line => owns(line, pending.operation) && id(line) === expected.variant && line.properties[ROLE] === expected.role && Number(line.quantity) === expected.quantity)).length || 0, hasSelected, giftVerified: !!gift, ownedCapCount: ownedCaps.length, ownedCapsFree, nested, committed: !pending || committed(cart, pending) });
      if (!hasSelected || !gift || !ownedCapsFree || !nested || (pending && !committed(cart, pending))) throw new Error('The free-cap offer did not apply');
      clear();
      return result(plan.items.length ? 'added' : 'already_applied', plan.reusesCap
        ? 'Your existing cap qualifies. Review the applied offer in your bag.'
        : 'The jacket and qualifying cap are in your bag. Review the applied offer before checkout.', { cart, gift, plan });
    } catch {
      if (writeAttempted) {
        if (pending?.uncertain) return result('review_required', 'The earlier cart request is still unconfirmed. Retry recovery before checkout; do not add the offer again.');
        try {
          const cart = await rollback(operation);
          clear();
          return result('unavailable', 'The offer could not be completed, so its newly added items were removed. Your existing items were kept.', { cart });
        } catch {
          return result('review_required', 'We could not verify your cart. Retry cart recovery before checkout; do not submit the offer again.');
        }
      }
      release();
      return result('unavailable', 'The offer is unavailable right now. Your cart was not changed.');
    } finally { finish?.(); busy = false; }
  }
  return { claim, recover, get busy() { return busy; }, get recoveryRequired() { return !!pending; } };
}

const activeOffers = new WeakSet();
const heldOffers = new WeakMap();
const HANDLES = [
  'new-era-nba-25-city-edition-atlanta-hawks-snapfront-jacket',
  'new-era-9fifty-nba-25-atlanta-hawks-snapback',
];

function sessionCheckpoint(storage, key = 'otf-peachtree-pending-v1') {
  return { read: () => { const saved = storage.getItem(key); if (!saved) return null; try { return JSON.parse(saved); } catch { return saved; } }, write: record => storage.setItem(key, JSON.stringify(record)), clear: () => storage.removeItem(key) };
}

function createThemeAdapter({ CartItems, routes, root, blockMutations, paymentWrappers = [], diagnostic = () => {} }) {
  if (!['beginMutation', 'holdCheckout', 'fetchCartText', 'reconcileCart'].every(name => typeof CartItems?.[name] === 'function') || typeof blockMutations !== 'function' || !routes?.cart_add_url || !routes?.cart_change_url || !root?.startsWith('/')) throw new Error('Required theme cart integration is unavailable');
  const localeRoot = root.endsWith('/') ? root : `${root}/`;
  const owner = Symbol('peachtree-offer');
  const otherOwner = () => heldOffers.has(CartItems) && heldOffers.get(CartItems) !== owner;
  const trace = (stage, values = {}) => { try { diagnostic(stage, values); } catch {} };
  const errorKind = error => ['AbortError', 'SyntaxError', 'TypeError'].includes(error?.name) ? error.name : 'Error';
  const request = async (url, body, kind) => {
    const started = Date.now();
    trace('request_start', { kind });
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    const options = body ? { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : { headers };
    let responseText;
    try { responseText = await CartItems.fetchCartText(url, options, new AbortController(), true); }
    catch (error) { trace('request_failed', { kind, outcome: 'transport_error', errorKind: errorKind(error), durationMs: Date.now() - started }); throw error; }
    const responseKind = typeof responseText === 'string' ? 'string' : 'other';
    const trimmed = responseKind === 'string' ? responseText.trimStart() : '';
    const responseFormat = responseKind !== 'string' ? 'other' : !trimmed ? 'empty' : /^[{[]/.test(trimmed) ? 'json_like' : trimmed.startsWith('<') ? 'html_like' : 'other';
    trace('request_returned', { kind, durationMs: Date.now() - started, responseKind, responseFormat, responseLength: responseKind === 'string' ? responseText.length : 0 });
    let data;
    try { data = JSON.parse(responseText); }
    catch (error) { trace('request_failed', { kind, outcome: 'parse_error', errorKind: errorKind(error), durationMs: Date.now() - started }); throw error; }
    trace('request_parsed', { kind, bodyStatus: Number.isInteger(data?.status) && data.status >= 100 && data.status <= 599 ? data.status : 0, reportedError: !!(data?.status >= 400 || data?.errors) });
    if (data.status >= 400 || data.errors) {
      const error = new Error(data.description || data.message || 'Cart request failed');
      if ([400, 422].includes(Number(data.status)) && (typeof data.description === 'string' || typeof data.message === 'string' || typeof data.errors === 'object')) error.cartWriteOutcome = 'rejected';
      throw error; // A 5xx/transient response is uncertain, never proof of no commit.
    }
    return data;
  };
  return {
    isBusy: () => !!(otherOwner() || activeOffers.has(CartItems) || CartItems.recoveryRelease || CartItems.mutationsInFlight || CartItems.pendingQuantityChanges),
    begin: () => {
      if (otherOwner() || activeOffers.has(CartItems) || CartItems.recoveryRelease || CartItems.mutationsInFlight || CartItems.pendingQuantityChanges) throw new Error('Cart is busy');
      activeOffers.add(CartItems);
      let finish;
      try { finish = CartItems.beginMutation(); } catch (error) { activeOffers.delete(CartItems); throw error; }
      return () => { try { finish(); } finally { activeOffers.delete(CartItems); } };
    },
    holdCheckout: () => {
      if (otherOwner()) throw new Error('Cart is busy');
      heldOffers.set(CartItems, owner);
      let unblock; let release;
      try { unblock = blockMutations(); release = CartItems.holdCheckout(); }
      catch (error) { unblock?.(); heldOffers.delete(CartItems); throw error; }
      const previous = paymentWrappers.map(node => ({ node, inert: node.inert }));
      previous.forEach(({ node }) => { node.inert = true; });
      return () => { previous.forEach(({ node, inert }) => { node.inert = inert; }); release(); unblock(); if (heldOffers.get(CartItems) === owner) heldOffers.delete(CartItems); };
    },
    read: () => request(`${localeRoot}cart.js`, undefined, 'cart_read'),
    add: items => request(`${localeRoot}cart/add.js`, { items }, 'cart_add'),
    change: (key, quantity) => request(`${localeRoot}cart/change.js`, { id: key, quantity }, 'cart_change'),
    availability: async ids => {
      const products = await Promise.all(HANDLES.map(handle => request(`${localeRoot}products/${handle}.js`, undefined, 'product_availability')));
      const wanted = new Set(ids);
      const answer = {};
      products.forEach(product => (product.variants || []).forEach(variant => {
        if (wanted.has(String(variant.id))) answer[String(variant.id)] = variant.available === true;
      }));
      return answer;
    },
    refresh: () => CartItems.reconcileCart(null, true),
  };
}

const INERT_TARGETS = [
  'product-form form', 'form[data-type="add-to-cart-form"]',
  'quantity-input', 'cart-remove-button',
  'variant-radios', 'variant-selects', '.otf-native-payment',
  '[data-otf-card-sizes]', '[data-otf-size-add]', '.quick--add-form', '.quick--add-to-cart', '.quick--add.popup--close',
].join(',');
const EVENT_TARGETS = `${INERT_TARGETS},form#cart,form#CartPopup-Form,.additional-checkout-buttons,.shopify-payment-button,button[name="checkout"][form="cart"],button[name="checkout"][form="CartPopup-Form"]`;
const RECOVERY_TARGETS = '#cart-errors button[type="button"][data-otf-cart-recovery],#CartPopup-CartErrors button[type="button"][data-otf-cart-recovery],#cart-errors a[href],#CartPopup-CartErrors a[href]';
const EVENTS = ['pointerdown', 'click', 'keydown', 'beforeinput', 'input', 'change', 'submit'];
const managers = new WeakMap();

function blockNativeMutations(document) {
  let manager = managers.get(document);
  if (!manager) {
    const Observer = document.defaultView?.MutationObserver;
    if (!Observer || !document.documentElement) throw new Error('Native mutation gate is unavailable');
    const states = new Map();
    let count = 0;
    let active = true;
    const process = records => {
      for (const record of records) {
        if (record.type !== 'attributes') continue;
        const state = states.get(record.target);
        if (!state) continue;
        if (state.ownWrites) state.ownWrites -= 1;
        else state.restore = record.target.getAttribute('inert');
      }
    };
    const scan = () => {
      document.querySelectorAll(INERT_TARGETS).forEach(node => {
        let state = states.get(node);
        if (!state) { state = { restore: node.getAttribute('inert'), ownWrites: 0 }; states.set(node, state); }
        if (!node.hasAttribute('inert')) { state.ownWrites += 1; node.setAttribute('inert', ''); }
      });
    };
    const observer = new Observer(records => { if (active) { process(records); scan(); } });
    const guard = event => {
      if (event.type === 'keydown' && ['Tab', 'Escape'].includes(event.key)) return;
      const path = event.composedPath?.() || [event.target];
      if (event.type !== 'submit' && path.some(node => node?.closest?.(RECOVERY_TARGETS))) return;
      const guarded = path.some(node => node?.closest?.(EVENT_TARGETS) || node?.form?.matches?.(EVENT_TARGETS));
      if (!guarded) return;
      if (event.cancelable) event.preventDefault();
      event.stopImmediatePropagation();
    };
    EVENTS.forEach(type => document.addEventListener(type, guard, true));
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['inert'], attributeOldValue: true });
    scan();
    manager = {
      acquire() {
        count += 1;
        let released = false;
        return () => {
          if (released) return;
          released = true;
          if (--count) return;
          process(observer.takeRecords());
          active = false;
          observer.disconnect();
          EVENTS.forEach(type => document.removeEventListener(type, guard, true));
          states.forEach((state, node) => {
            if (state.restore === null) node.removeAttribute('inert');
            else if (node.getAttribute('inert') !== state.restore) node.setAttribute('inert', state.restore);
          });
          states.clear();
          managers.delete(document);
        };
      },
    };
    managers.set(document, manager);
  }
  return manager.acquire();
}

  const BUILD_ID = 'peachtree-named-allocation-r1-20261017';
  const bootstrap = document.querySelector('script[data-otf-peachtree-bootstrap]');
  if (!bootstrap) return;
  bootstrap.setAttribute('data-otf-offer-build', BUILD_ID);
  if (window.__otfPeachtreeOffer) return;
  const text = key => bootstrap.dataset[key] || '';
  let checkpoint;
  let saved;
  let module = findModule();
  let api;
  let flow;
  let current = null;
  let proof = null;
  let globalRecovery = null;
  let running = false;
  let fatal = false;
  let lastResult = null;
  try { checkpoint = sessionCheckpoint(window.sessionStorage); saved = checkpoint.read(); }
  catch { showSetupError(); return; }
  if (!saved && !module) return;
  const diagnosticStarted = Date.now();
  const diagnosticRecords = [];
  let diagnosticMirror = null;
  const mirrorDiagnostics = () => {
    try { if (diagnosticMirror) diagnosticMirror.textContent = JSON.stringify({ build: BUILD_ID, records: diagnosticRecords }); } catch {}
  };
  try {
    diagnosticMirror = document.createElement('script');
    diagnosticMirror.setAttribute('type', 'application/json');
    diagnosticMirror.id = 'OtfPeachtreeDiagnostics';
    diagnosticMirror.setAttribute('data-otf-offer-diagnostics', '');
    diagnosticMirror.setAttribute('data-otf-offer-build', BUILD_ID);
    diagnosticMirror.hidden = true;
    document.body.append(diagnosticMirror);
    mirrorDiagnostics();
  } catch { diagnosticMirror = null; }
  const diagnostic = (stage, values = {}) => {
    const record = { stage, elapsedMs: Math.max(0, Date.now() - diagnosticStarted) };
    for (const key of ['expectedCount', 'returnedCount', 'matchedCount', 'ownedCapCount', 'durationMs', 'bodyStatus', 'responseLength', 'capOriginalCents', 'capPriceCents', 'capFinalLineCents', 'capQuantity', 'capAllocationCount', 'capTitleMatchCount', 'capAutomaticMatchCount', 'capMatchedDiscountCents', 'capLineCents', 'capAllAllocationCents', 'capExpectedTitleDiscountCents', 'capEligibleMatchCount']) if (Number.isSafeInteger(values[key]) && values[key] >= 0) record[key] = values[key];
    for (const key of ['settled', 'uncertain', 'hasSelected', 'giftVerified', 'ownedCapsFree', 'nested', 'committed', 'reportedError', 'refreshOk', 'capOriginalPresent', 'capFinalPresent', 'capAllocationArray', 'capTitleMatches', 'capAutomaticMatches', 'capOriginalPositive', 'capAllocationAmountsValid', 'capArithmeticMatches', 'capExactlyOneFreeUnit', 'capLinePresent', 'capAllAmountsValid', 'capEligibleMatches']) if (typeof values[key] === 'boolean') record[key] = values[key];
    if (['cart_read', 'cart_add', 'cart_change', 'product_availability'].includes(values.kind)) record.kind = values.kind;
    if (['transport_error', 'parse_error', 'busy', 'unchanged', 'review_required', 'unavailable', 'disabled', 'selection_required', 'added', 'already_applied'].includes(values.outcome)) record.outcome = values.outcome;
    if (['AbortError', 'SyntaxError', 'TypeError', 'Error'].includes(values.errorKind)) record.errorKind = values.errorKind;
    if (['string', 'other'].includes(values.responseKind)) record.responseKind = values.responseKind;
    if (['json_like', 'html_like', 'empty', 'other'].includes(values.responseFormat)) record.responseFormat = values.responseFormat;
    diagnosticRecords.push(record);
    if (diagnosticRecords.length > 60) diagnosticRecords.shift();
    mirrorDiagnostics();
  };
  window.__otfPeachtreeOffer = { get diagnostics() { return diagnosticRecords.map(record => ({ ...record })); }, get busy() { return running || !!flow?.busy; }, get recoveryRequired() { return !!saved || !!flow?.recoveryRequired; } };
  const NativeCart = typeof CartItems === 'undefined' ? null : CartItems;
  const validSaved = !saved || typeof saved === 'object' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(saved.operation || '') && typeof saved.uncertain === 'boolean' && Array.isArray(saved.expected) && saved.expected.length > 0 && saved.expected.length <= 2 && saved.expected.every(item => item.quantity === 1 && (item.role === 'jacket' && JACKET_VARIANTS.includes(item.variant) || item.role === 'cap' && item.variant === CAP_VARIANT)) && new Set(saved.expected.map(item => item.role)).size === saved.expected.length;
  if (!validSaved || !NativeCart || !window.Shopify?.routes?.root || typeof routes === 'undefined') { failSetup(); return; }
  const canGenerateToken = typeof window.crypto?.randomUUID === 'function';
  try {
    api = createThemeAdapter({ CartItems: NativeCart, routes, root: window.Shopify.routes.root, blockMutations: () => blockNativeMutations(document), paymentWrappers: [], diagnostic });
    flow = createOfferFlow({ api, enabled: !!module, discountTitle: 'Peachtree Jacket + Free Cap', checkpoint, token: () => window.crypto.randomUUID(), diagnostic });
  } catch { failSetup(); return; }

  function findModule() {
    const nodes = document.querySelectorAll('[data-otf-peachtree-offer]');
    return nodes.length === 1 ? nodes[0] : null;
  }
  function configFor(node) {
    try {
      const config = JSON.parse(node.querySelector('[data-otf-offer-config]').textContent);
      if (String(config.productId) !== '10262889889973' || config.handle !== 'new-era-nba-25-city-edition-atlanta-hawks-snapfront-jacket' || config.sectionId !== node.dataset.section || config.formId !== `product-form-${config.sectionId}` || config.discountTitle !== 'Peachtree Jacket + Free Cap' || !Array.isArray(config.variants)) return null;
      const ids = config.variants.map(variant => String(variant.id));
      if (new Set(ids).size !== ids.length || ids.some(value => !JACKET_VARIANTS.includes(value))) return null;
      return config;
    } catch { return null; }
  }
  function stateFor(node) {
    const config = configFor(node);
    if (!config) return null;
    const root = document.getElementById(`MainProduct-${config.sectionId}`);
    const info = root?.querySelector(`product-info[data-section="${CSS.escape(config.sectionId)}"]`);
    const form = document.getElementById(config.formId);
    const picker = info?.querySelector('variant-radios, variant-selects');
    if (!root?.contains(node) || !root.contains(form) || form?.closest('[id^="MainProduct-"]') !== root || !picker || !info.dataset.url?.endsWith(`/products/${config.handle}`)) return null;
    const groups = [...picker.querySelectorAll('[data-otf-option]')].sort((a, b) => Number(a.dataset.otfOption) - Number(b.dataset.otfOption));
    const choices = groups.map(group => group.tagName === 'SELECT' ? group.value : group.querySelector('input:checked')?.value || '');
    const size = groups.find(group => group.hasAttribute('data-otf-size'));
    const sizeValue = size?.tagName === 'SELECT' ? size.value : size?.querySelector('input:checked')?.value || '';
    const input = form.querySelector('input[name="id"]');
    const variant = config.variants.find(item => String(item.id) === String(input?.value));
    const ready = groups.length > 0 && choices.every(Boolean) && input && !input.disabled && variant?.available === true && Array.isArray(variant.options) && variant.options.length === choices.length && variant.options.every((value, index) => value === choices[index]) && root.dataset.otfSelectionLoading === 'false' && root.dataset.otfSizePending === 'false' && root.dataset.otfVariantUnavailable === 'false' && info.dataset.otfNeedsSize === 'false';
    return { config, root, info, picker, form, sizeValue, variant, ready };
  }
  function chosen() {
    const state = module && stateFor(module);
    return state?.ready && proof && proof.root === state.root && proof.size === state.sizeValue ? state : null;
  }
  function put(node, value) { if (node && node.textContent !== value) node.textContent = value; }
  function failSetup() {
    fatal = true;
    showSetupError();
    if (saved) {
      blockNativeMutations(document);
      NativeCart?.holdCheckout?.();
      document.querySelectorAll('button[name="checkout"]').forEach(button => { button.disabled = true; });
      const host = recoveryHost();
      put(host.querySelector('[data-otf-offer-status]'), text('reviewRequired'));
      const retry = host.querySelector('[data-otf-offer-recover]');
      if (retry) retry.hidden = true;
      const help = host.querySelector('[data-otf-offer-assistance]');
      if (help) help.hidden = false;
    }
  }
  function showSetupError() {
    document.querySelectorAll('[data-otf-peachtree-offer]').forEach(node => {
      const status = node.querySelector('[data-otf-offer-status]');
      put(status, text('setupError'));
      node.querySelector('[data-otf-offer-claim]')?.setAttribute('disabled', '');
      const help = node.querySelector('[data-otf-offer-assistance]');
      if (help) help.hidden = false;
    });
  }
  function recoveryHost() {
    if (module) return module;
    if (!globalRecovery) {
      globalRecovery = document.createElement('aside');
      globalRecovery.setAttribute('aria-labelledby', 'OtfPeachtreeRecoveryHeading');
      globalRecovery.setAttribute('data-otf-offer-recovery-host', '');
      globalRecovery.style.cssText = 'margin:16px;padding:16px;border:1px solid currentColor;border-radius:8px;line-height:1.5;';
      const heading = document.createElement('h2');
      heading.id = 'OtfPeachtreeRecoveryHeading';
      heading.textContent = text('recoveryHeading');
      const status = document.createElement('p');
      status.setAttribute('data-otf-offer-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.setAttribute('data-otf-offer-recover', '');
      retry.textContent = text('retryRecovery');
      retry.style.cssText = 'min-height:44px;padding:10px;font:inherit;';
      const help = document.createElement('a');
      help.setAttribute('data-otf-offer-assistance', '');
      help.href = bootstrap.dataset.helpUrl;
      help.textContent = text('assistance');
      help.style.cssText = 'display:inline-flex;align-items:center;min-height:44px;margin-inline-start:12px;';
      const bag = document.createElement('a');
      bag.setAttribute('data-otf-offer-view-cart', '');
      bag.href = bootstrap.dataset.cartUrl;
      bag.textContent = text('reviewBag');
      bag.hidden = true;
      bag.style.cssText = 'display:inline-flex;align-items:center;min-height:44px;margin-inline-start:12px;';
      const style = document.createElement('style');
      style.textContent = '[data-otf-offer-recovery-host] [hidden]{display:none!important}[data-otf-offer-recovery-host] :is(button,a):focus-visible{outline:2px solid currentColor;outline-offset:3px}';
      globalRecovery.append(style, heading, status, retry, help, bag);
      (document.getElementById('MainContent') || document.querySelector('main') || document.body).prepend(globalRecovery);
    }
    return globalRecovery;
  }
  function revealBagReview() {
    const host = module || recoveryHost();
    const link = host.querySelector('[data-otf-offer-view-cart]');
    if (link) link.hidden = false;
  }
  function announce(result) {
    lastResult = result;
    const host = flow.recoveryRequired || !module ? recoveryHost() : module;
    const key = result.status === 'selection_required' ? 'chooseSize' : result.status === 'added' ? result.plan?.reusesCap ? 'reused' : 'added' : result.status === 'unavailable' && result.recovered ? 'recovered' : result.status.replace(/_([a-z])/g, (_, value) => value.toUpperCase());
    put(host.querySelector('[data-otf-offer-status]'), text(key));
    const assistance = host.querySelector('[data-otf-offer-assistance]');
    if (assistance) assistance.hidden = !flow.recoveryRequired && !['refresh_error', 'setup_error'].includes(result.status);
    sync();
  }
  function sync() {
    const next = findModule();
    if (module !== next) { module = next; proof = null; current = null; }
    if (module) {
      current = stateFor(module);
      const selected = chosen();
      const button = module.querySelector('[data-otf-offer-claim]');
      const disabled = !canGenerateToken || !selected || running || flow.busy || flow.recoveryRequired || api.isBusy();
      if (button) { button.disabled = disabled; button.setAttribute('aria-disabled', String(disabled)); button.setAttribute('aria-busy', String(running || flow.busy)); }
      const label = module.querySelector('[data-otf-offer-selection]');
      put(label, selected || current?.ready ? text('selectedSize').replace('[size]', (selected || current).sizeValue) : text('chooseSize'));
      const price = module.querySelector('[data-otf-offer-jacket-price]');
      if (price && current?.ready && typeof current.variant.priceLabel === 'string') put(price, current.variant.priceLabel);
      const confirm = module.querySelector('[data-otf-offer-confirm-size]');
      if (confirm) {
        confirm.hidden = !!selected || current?.picker.localName !== 'variant-selects' || !current?.ready;
        confirm.disabled = running || flow.busy || flow.recoveryRequired || api.isBusy();
        put(confirm, (confirm.dataset.label || '').replace('[size]', current?.sizeValue || ''));
      }
      if (!canGenerateToken && !flow.recoveryRequired) showSetupError();
    }
    if (flow.recoveryRequired) {
      const host = recoveryHost();
      put(host.querySelector('[data-otf-offer-status]'), running || flow.busy ? text('busy') : lastResult?.status === 'refresh_error' ? text('refreshError') : lastResult?.status === 'setup_error' ? text('setupError') : saved?.uncertain === false ? text('recoveryHeading') : text('reviewRequired'));
      const help = host.querySelector('[data-otf-offer-assistance]');
      if (help) help.hidden = false;
    }
    const hosts = [module, globalRecovery].filter(Boolean);
    hosts.forEach(host => {
      const retry = host.querySelector('[data-otf-offer-recover]');
      if (retry) { retry.hidden = !flow.recoveryRequired; retry.disabled = running || flow.busy; }
    });
  }
  async function refreshCart() {
    try { const ok = await api.refresh() === true; diagnostic('refresh_result', { refreshOk: ok }); return ok; } catch { diagnostic('refresh_result', { refreshOk: false }); return false; }
  }
  async function perform(recovery, trigger) {
    if (fatal || running || flow.busy) return;
    const selected = recovery ? null : chosen();
    if (!recovery && (!selected || !canGenerateToken)) { announce({ status: 'selection_required' }); return; }
    running = true;
    const unblock = blockNativeMutations(document);
    announce({ status: 'busy' });
    try {
      if (NativeCart.recoveryRelease && !await refreshCart()) { announce({ status: 'refresh_error' }); return; }
      const pending = recovery ? flow.recover() : flow.claim({ variantId: selected.variant.id, explicitSelection: true });
      sync();
      const result = await pending; saved = flow.recoveryRequired ? checkpoint.read() : null;
      if (recovery && result.status === 'unavailable') result.recovered = true;
      if (result.cart || flow.recoveryRequired) {
        if (!await refreshCart()) { announce({ status: 'refresh_error' }); return; }
      }
      announce(result);
      if (['added', 'already_applied'].includes(result.status)) {
        const popup = document.querySelector('cart-popup');
        const wrapper = popup?.closest('[data-popup-type="cart-popup"]');
        try {
          if (!popup || !wrapper) revealBagReview();
          else if (wrapper.classList.contains('hide-popup')) popup.open(trigger);
        } catch { revealBagReview(); }
      }
    } catch { announce({ status: flow.recoveryRequired ? 'review_required' : 'setup_error' }); }
    finally { unblock(); running = false; sync(); }
  }
  const onClick = event => {
    const claim = event.target.closest?.('[data-otf-offer-claim]');
    const recover = event.target.closest?.('[data-otf-offer-recover]');
    const confirm = event.target.closest?.('[data-otf-offer-confirm-size]');
    if (confirm && module?.contains(confirm)) {
      event.preventDefault();
      const state = stateFor(module);
      if (event.isTrusted && !confirm.disabled && state?.ready && state.picker.localName === 'variant-selects' && !running && !flow.busy && !flow.recoveryRequired && !api.isBusy()) proof = { root: state.root, size: state.sizeValue };
      sync(); return;
    }
    if (event.isTrusted && event.target.matches?.('input[type="radio"]') && event.target.checked) rememberSize(event);
    if (claim && module?.contains(claim)) { event.preventDefault(); if (!claim.disabled) perform(false, claim); }
    else if (recover && (module?.contains(recover) || globalRecovery?.contains(recover))) { event.preventDefault(); if (!recover.disabled) perform(true, recover); }
  };
  function rememberSize(event) {
    if (!module) return;
    const state = stateFor(module);
    if (!state) { proof = null; sync(); return; }
    const group = event.target.closest?.('[data-otf-size]');
    const owner = group?.closest('variant-radios, variant-selects');
    const validPicker = owner === state.picker || state.root.contains(owner) && (owner?.id === `variant-radios-${state.config.sectionId}-sticky` || owner?.id === `variant-selects-${state.config.sectionId}-sticky`);
    if (group && validPicker) {
      const size = group.tagName === 'SELECT' ? group.value : group.querySelector('input:checked')?.value;
      if (event.isTrusted) proof = size ? { root: state.root, size } : null;
      else if (proof?.size !== size) proof = null;
    }
    if (state.root.contains(event.target) || validPicker) {
      const button = module.querySelector('[data-otf-offer-claim]');
      if (button) { button.disabled = true; button.setAttribute('aria-disabled', 'true'); }
      queueMicrotask(sync);
    }
  }
  const onChange = event => rememberSize(event);
  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange, true);
  let syncQueued = false;
  const scheduleSync = () => { if (!syncQueued) { syncQueued = true; queueMicrotask(() => { syncQueued = false; sync(); }); } };
  const observer = new MutationObserver(records => {
    if (records.some(record => record.type === 'childList' || record.target?.matches?.('[data-otf-size-pending], [data-otf-selection-loading], [data-otf-variant-unavailable]'))) scheduleSync();
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-otf-size-pending', 'data-otf-selection-loading', 'data-otf-variant-unavailable'] });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.variantChange, scheduleSync);
  window.addEventListener('pageshow', sync);
  document.addEventListener('shopify:section:load', sync);
  document.addEventListener('shopify:section:unload', scheduleSync);
  sync();
  if (flow.recoveryRequired) { announce({ status: 'review_required' }); perform(true); }

})();
